/**
 * Self-service password recovery client.
 *
 * Talks to the auth-recovery Lambda (Function URL) instead of Cognito's
 * ForgotPassword API. Cognito cannot deliver recovery mail to this tenant and
 * sends nothing at all for FORCE_CHANGE_PASSWORD accounts, so the endpoint mints
 * and mails its own code, then sets the password with AdminSetUserPassword.
 */

const BASE_URL = (process.env.REACT_APP_AUTH_RECOVERY_URL || '').replace(/\/+$/, '');

const post = async (body: Record<string, unknown>) => {
  if (!BASE_URL) {
    throw new Error('Password recovery is not configured for this build. Please contact support.');
  }

  let res: Response;
  try {
    res = await fetch(BASE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('We could not reach the recovery service. Check your connection and try again.');
  }

  let data: any = {};
  try {
    data = await res.json();
  } catch {
    /* non-JSON error body */
  }

  if (!res.ok || data?.ok === false) {
    throw new Error(data?.message || 'Something went wrong. Please try again.');
  }
  return data;
};

/** Asks for a reset code. Resolves for any well-formed address (no enumeration). */
export const requestResetCode = (email: string) => post({ action: 'request', email: email.trim() });

/** Verifies the code and sets the new password permanently. */
export const confirmResetPassword = (email: string, code: string, newPassword: string) =>
  post({
    action: 'confirm',
    email: email.trim(),
    // Codes copied from email can carry spaces or formatting.
    code: String(code).replace(/\D/g, ''),
    password: newPassword,
  });
